import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Monitor, MonitorSpeaker, ZoomIn, ZoomOut, RefreshCw,
  Grid3x3, Grid2x2, Settings, ChevronLeft, ChevronRight,
  Plus, X, MonitorSmartphone, Maximize2, Minimize2
} from 'lucide-react';
import type { Product } from '@shared/schema';

// Better image mapping for meat products
const getMeatImage = (description: string): string => {
  const desc = description.toLowerCase();
  
  // Specific mappings for better images
  if (desc.includes('striploin') || desc.includes('strip loin')) return 'https://images.unsplash.com/photo-1588347818036-558601350947?w=400&h=300&fit=crop';
  if (desc.includes('short rib')) return 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=400&h=300&fit=crop';
  if (desc.includes('ground beef')) return 'https://images.unsplash.com/photo-1603048297172-c92544798d5a?w=400&h=300&fit=crop';
  if (desc.includes('pork belly')) return 'https://images.unsplash.com/photo-1641558478751-ad448f3c79fe?w=400&h=300&fit=crop';
  if (desc.includes('pork shoulder')) return 'https://images.unsplash.com/photo-1623047437095-27418540c288?w=400&h=300&fit=crop';
  if (desc.includes('pork chop')) return 'https://images.unsplash.com/photo-1609501676725-7186f017a4b7?w=400&h=300&fit=crop';
  if (desc.includes('ribs')) return 'https://images.unsplash.com/photo-1544025162-d76694265947?w=400&h=300&fit=crop';
  if (desc.includes('whole chicken')) return 'https://images.unsplash.com/photo-1587593810167-a84920ea0781?w=400&h=300&fit=crop';
  if (desc.includes('chicken breast')) return 'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=400&h=300&fit=crop';
  if (desc.includes('chicken wings')) return 'https://images.unsplash.com/photo-1527477396000-e27163b481c2?w=400&h=300&fit=crop';
  if (desc.includes('drumstick')) return 'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=400&h=300&fit=crop';
  if (desc.includes('hot pot') || desc.includes('slice')) return 'https://images.unsplash.com/photo-1625944525533-473f1a3d54e7?w=400&h=300&fit=crop';
  
  // Fallback
  return `https://source.unsplash.com/400x300/?${encodeURIComponent(description)},meat,food`;
};

// Add CSS for animations
const styles = `
  @keyframes blob {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }
  .animate-blob {
    animation: blob 7s infinite;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
`;

interface SubScreen {
  id: string;
  categories: string[];
  products: Product[];
  columns: number;
  rows: number;
}

interface ScreenConfig {
  id: number;
  name: string;
  leftScreen: SubScreen;
  rightScreen: SubScreen;
}

interface DisplaySettings {
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
  activeScreen: number;
  screenMode: 'single' | 'dual' | 'triple';
}

export default function MeatPricesDisplay() {
  // Inject styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);
  const [screens, setScreens] = useState<ScreenConfig[]>([
    { 
      id: 1, 
      name: 'Screen 1',
      leftScreen: { id: '1-left', categories: ['meat-beef'], products: [], columns: 3, rows: 0 },
      rightScreen: { id: '1-right', categories: ['meat-chicken'], products: [], columns: 3, rows: 0 }
    },
    { 
      id: 2, 
      name: 'Screen 2',
      leftScreen: { id: '2-left', categories: ['meat-pork'], products: [], columns: 3, rows: 0 },
      rightScreen: { id: '2-right', categories: ['meat-hotpot'], products: [], columns: 3, rows: 0 }
    },
    { 
      id: 3, 
      name: 'Screen 3',
      leftScreen: { id: '3-left', categories: [], products: [], columns: 3, rows: 0 },
      rightScreen: { id: '3-right', categories: [], products: [], columns: 3, rows: 0 }
    }
  ]);

  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>({
    fontSize: 'large',
    activeScreen: 1,
    screenMode: 'single'
  });
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch all meat products from retail products
  const { data: allProducts, isLoading, refetch } = useQuery({
    queryKey: ['meat-products'],
    queryFn: async () => {
      // Fetch all products and filter for meat categories
      const allProductsResponse = await apiRequest<Product[]>('/api/products');
      const meatCategories = ['meat-beef', 'meat-pork', 'meat-chicken', 'meat-hotpot'];
      return allProductsResponse.filter(p => 
        p.active && meatCategories.includes(p.category)
      );
    },
    refetchInterval: 60000 // Auto-refresh every minute
  });

  // Fetch admin settings for last updated time
  const { data: adminSettings } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      return await apiRequest<{ lastUpdated: string }>('/api/admin/settings');
    },
    refetchInterval: 60000 // Auto-refresh every minute
  });

  // Load saved configuration or prepopulate screens
  useEffect(() => {
    const savedConfig = localStorage.getItem('meatPricesScreenConfig');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        setScreens(parsed);
      } catch (e) {
        console.error('Failed to load saved config', e);
      }
    }
  }, []);
  
  // Update products when they change
  useEffect(() => {
    if (allProducts && allProducts.length > 0) {
      setScreens(prev => prev.map(screen => ({
        ...screen,
        leftScreen: {
          ...screen.leftScreen,
          products: allProducts.filter(p => screen.leftScreen.categories.includes(p.category))
        },
        rightScreen: {
          ...screen.rightScreen,
          products: allProducts.filter(p => screen.rightScreen.categories.includes(p.category))
        }
      })));
    }
  }, [allProducts]);

  // Get unassigned products
  const assignedProductIds = new Set(
    screens.flatMap(screen => [
      ...screen.leftScreen.products.map(p => p.id),
      ...screen.rightScreen.products.map(p => p.id)
    ])
  );
  const unassignedProducts = allProducts?.filter(p => !assignedProductIds.has(p.id)) || [];


  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes (including ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);


  // Update subscreen columns
  const updateSubScreenColumns = (screenId: number, subScreenId: string, columns: number) => {
    setScreens(prev => prev.map(screen => {
      if (screen.id === screenId) {
        if (subScreenId.endsWith('-left')) {
          return { ...screen, leftScreen: { ...screen.leftScreen, columns } };
        } else {
          return { ...screen, rightScreen: { ...screen.rightScreen, columns } };
        }
      }
      return screen;
    }));
  };
  
  // Update subscreen rows
  const updateSubScreenRows = (screenId: number, subScreenId: string, rows: number) => {
    setScreens(prev => prev.map(screen => {
      if (screen.id === screenId) {
        if (subScreenId.endsWith('-left')) {
          return { ...screen, leftScreen: { ...screen.leftScreen, rows } };
        } else {
          return { ...screen, rightScreen: { ...screen.rightScreen, rows } };
        }
      }
      return screen;
    }));
  };
  
  // Update subscreen categories
  const updateSubScreenCategories = (screenId: number, subScreenId: string, categories: string[]) => {
    setScreens(prev => prev.map(screen => {
      if (screen.id === screenId) {
        if (subScreenId.endsWith('-left')) {
          return { 
            ...screen, 
            leftScreen: { 
              ...screen.leftScreen, 
              categories,
              products: allProducts?.filter(p => categories.includes(p.category)) || []
            } 
          };
        } else {
          return { 
            ...screen, 
            rightScreen: { 
              ...screen.rightScreen, 
              categories,
              products: allProducts?.filter(p => categories.includes(p.category)) || []
            } 
          };
        }
      }
      return screen;
    }));
  };

  // Auto-calculate grid columns based on product count
  const calculateOptimalColumns = (productCount: number): number => {
    if (productCount <= 3) return 1;
    if (productCount <= 8) return 2;
    if (productCount <= 15) return 3;
    if (productCount <= 24) return 4;
    return 5;
  };

  const fontSizeClasses = {
    small: 'text-lg',
    medium: 'text-xl',
    large: 'text-2xl',
    xlarge: 'text-3xl'
  };

  const priceFontSizeClasses = {
    small: 'text-2xl',
    medium: 'text-3xl',
    large: 'text-4xl',
    xlarge: 'text-5xl'
  };

  const SubScreenGrid = ({ subScreen, screenId, isFullscreen }: { subScreen: SubScreen, screenId: number, isFullscreen: boolean }) => {
    const autoColumns = calculateOptimalColumns(subScreen.products.length);
    const columns = subScreen.columns === 0 ? autoColumns : subScreen.columns;
    const availableCategories = ['meat-beef', 'meat-chicken', 'meat-pork', 'meat-hotpot'];
    
    const categoryNames = {
      'meat-beef': '🥩 BEEF CUTS',
      'meat-pork': '🐷 PORK CUTS', 
      'meat-chicken': '🐔 CHICKEN CUTS',
      'meat-hotpot': '🍲 HOT POT'
    };
    
    const categoryColors = {
      'meat-beef': { bg: 'bg-red-100', banner: 'bg-red-200', border: 'border-red-300' },
      'meat-chicken': { bg: 'bg-yellow-100', banner: 'bg-yellow-200', border: 'border-yellow-300' },
      'meat-pork': { bg: 'bg-pink-100', banner: 'bg-pink-200', border: 'border-pink-300' },
      'meat-hotpot': { bg: 'bg-orange-100', banner: 'bg-orange-200', border: 'border-orange-300' }
    };
    
    const getCategoryColor = () => {
      if (subScreen.categories.length === 1) {
        return categoryColors[subScreen.categories[0]] || { bg: 'bg-gray-950', banner: 'bg-red-600', border: 'border-gray-700' };
      }
      return { bg: 'bg-gray-950', banner: 'bg-red-600', border: 'border-gray-700' };
    };
    
    const colors = getCategoryColor();
    
    return (
      <div className={`${isFullscreen ? 'h-full' : 'h-full'} flex flex-col ${colors.bg} border-4 ${colors.border}`}>
        {/* Subscreen controls - hidden in fullscreen */}
        {!isFullscreen && (
        <div className="bg-black p-3 border-b-2 border-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Category selector */}
              <select
                value={subScreen.categories.join(',')}
                onChange={(e) => {
                  const value = e.target.value;
                  const newCategories = value ? value.split(',') : [];
                  updateSubScreenCategories(screenId, subScreen.id, newCategories);
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 rounded px-3 py-1 text-sm"
              >
                <option value="">Select category</option>
                <option value="meat-beef">🥩 Beef</option>
                <option value="meat-chicken">🐔 Chicken</option>
                <option value="meat-pork">🐷 Pork</option>
                <option value="meat-hotpot">🍲 Hotpot</option>
                <option value="meat-beef,meat-chicken">🥩 Beef + 🐔 Chicken</option>
                <option value="meat-pork,meat-hotpot">🐷 Pork + 🍲 Hotpot</option>
              </select>
              <span className="text-sm text-gray-400">
                {subScreen.products.length} items
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Column selector */}
              <select
                value={columns}
                onChange={(e) => updateSubScreenColumns(screenId, subScreen.id, parseInt(e.target.value))}
                className="bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 rounded px-2 py-1 text-sm"
              >
                <option value={0}>Auto</option>
                <option value={1}>1 col</option>
                <option value={2}>2 cols</option>
                <option value={3}>3 cols</option>
                <option value={4}>4 cols</option>
                <option value={5}>5 cols</option>
                <option value={6}>6 cols</option>
                <option value={7}>7 cols</option>
                <option value={8}>8 cols</option>
              </select>
              
              {/* Row selector */}
              <select
                value={subScreen.rows || 0}
                onChange={(e) => updateSubScreenRows(screenId, subScreen.id, parseInt(e.target.value))}
                className="bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 rounded px-2 py-1 text-sm"
              >
                <option value={0}>Auto rows</option>
                <option value={1}>1 row</option>
                <option value={2}>2 rows</option>
                <option value={3}>3 rows</option>
                <option value={4}>4 rows</option>
                <option value={5}>5 rows</option>
              </select>
            </div>
          </div>
        </div>
        )}
        
        {/* Category header if single category */}
        {subScreen.categories.length === 1 && (
          <div className={`${colors.banner} text-black py-2 px-4 text-center border-b-4 border-white shadow-lg`}>
            <h3 className="text-3xl font-black drop-shadow-lg">{categoryNames[subScreen.categories[0]]}</h3>
          </div>
        )}
        
        {/* Products grid */}
        <div className={`flex-1 ${isFullscreen ? 'p-2' : 'p-4'} overflow-auto`} style={{ 
          maxHeight: isFullscreen 
            ? 'calc(100vh - 240px)' // Optimized for larger header and category text
            : 'calc(100vh - 380px)' // Optimized for regular mode with all elements
        }}>
          {subScreen.products.length > 0 ? (
            <div 
              style={{ 
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gridTemplateRows: subScreen.rows > 0 ? `repeat(${subScreen.rows}, 1fr)` : undefined,
                gap: '0.75rem',
                height: '100%',
                alignContent: 'start'
              }}
            >
              {subScreen.products.slice(0, subScreen.rows > 0 ? subScreen.rows * columns : undefined).map((product) => (
                      <Card key={product.id} className="relative overflow-hidden bg-white text-white shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 flex flex-col h-full rounded-lg" style={{ border: '5px solid', borderImage: 'linear-gradient(45deg, #fde047, #facc15, #eab308, #facc15, #fde047) 1' }}>
                        {/* Image display - expandable to fill space */}
                        <div className="relative overflow-hidden flex-1" style={{ minHeight: '80px' }}>
                          <img
                            src={product.imageUrl || getMeatImage(product.description)}
                            alt={product.description}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src = 'https://images.unsplash.com/photo-1558030006-450675393462?w=400&h=300&fit=crop';
                            }}
                          />
                        </div>
                        
                        {/* Product info - FIXED size and positioning */}
                        <div className="p-2 bg-white flex flex-col justify-between" style={{ height: '120px', flexShrink: 0 }}>
                          <div>
                            <h3 className={`font-bold ${fontSizeClasses[displaySettings.fontSize]} leading-none text-black whitespace-nowrap overflow-hidden text-ellipsis uppercase`}>
                              {product.description}
                            </h3>
                            <p className="text-3xl text-yellow-700 font-black leading-none whitespace-nowrap overflow-hidden text-ellipsis font-serif">
                              {product.chineseName || '肉类'}
                            </p>
                          </div>
                          
                          {/* Price at bottom right */}
                          <div className="text-right -mt-3">
                            <span className="text-4xl font-black text-green-800">
                              ${product.retailPrice.toFixed(2)}
                            </span>
                            <span className="text-2xl text-green-700">/lb</span>
                          </div>
                        </div>
                    </Card>
                    ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p>No products assigned to this section</p>
              <p className="text-sm mt-2">Select a category above to display products</p>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  const ScreenDisplay = ({ screen, isFullscreen }: { screen: ScreenConfig, isFullscreen: boolean }) => {
    return (
      <div className={`${isFullscreen ? 'h-screen' : 'h-full'} flex flex-col bg-black relative`}>
        {/* Header with logo and title */}
        <div className={`flex items-center justify-between ${isFullscreen ? 'px-8 py-4' : 'px-12 py-6'} border-b-4 border-white`} style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
          <div className="flex-1">
            <h1 className={`text-yellow-600 font-black ${isFullscreen ? 'text-6xl' : 'text-7xl'} mb-2 font-serif tracking-tight text-left leading-none`}>
              TODAY'S PRICES
            </h1>
            <p className={`text-black font-black ${isFullscreen ? 'text-lg' : 'text-xl'} animate-pulse`} style={{ animationDuration: '3s' }}>
              Last updated: 
              <span className="text-green-800 ml-2">
                {adminSettings?.lastUpdated ? new Date(adminSettings.lastUpdated).toLocaleDateString() : new Date().toLocaleDateString()}
              </span>
              <span className="text-green-700 ml-2">
                {adminSettings?.lastUpdated ? new Date(adminSettings.lastUpdated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true}) : new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true})}
              </span>
            </p>
          </div>
          <div>
            <img 
              src="/images/TopsLogoCrop.png" 
              alt="TOPS SUPERMARKET" 
              className={`${isFullscreen ? 'h-28' : 'h-36'} w-auto drop-shadow-2xl rounded-2xl`}
            />
          </div>
        </div>
        
        {/* Sub screens side by side */}
        <div className="flex-1 flex">
          <div className="w-1/2 border-r-4 border-yellow-400">
            <SubScreenGrid subScreen={screen.leftScreen} screenId={screen.id} isFullscreen={isFullscreen} />
          </div>
          <div className="w-1/2">
            <SubScreenGrid subScreen={screen.rightScreen} screenId={screen.id} isFullscreen={isFullscreen} />
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-2xl">Loading meat prices...</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-black text-white relative overflow-hidden">
      
      {/* Control Panel */}
      {!isFullscreen && (
        <div className="relative z-10 bg-black p-4 border-b-2 border-white">
          {/* Fullscreen button centered at top */}
          <div className="absolute left-1/2 transform -translate-x-1/2 top-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={toggleFullscreen}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              Fullscreen Display
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-black text-white">
                🥩 Premium Meat Department
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
            </div>
            
            <div className="flex items-center gap-2">

            {/* Font Size */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const sizes: DisplaySettings['fontSize'][] = ['small', 'medium', 'large', 'xlarge'];
                const currentIndex = sizes.indexOf(displaySettings.fontSize);
                const nextIndex = (currentIndex + 1) % sizes.length;
                setDisplaySettings(prev => ({ ...prev, fontSize: sizes[nextIndex] }));
              }}
              className="bg-gray-700 hover:bg-gray-600 text-white"
              title={`Font size: ${displaySettings.fontSize}`}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>


            {/* Save State */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                localStorage.setItem('meatPricesScreenConfig', JSON.stringify(screens));
                alert('Configuration saved!');
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Save Config
            </Button>
            
            {/* Refresh */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => refetch()}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>

            </div>
          </div>
        </div>
      )}

      {/* Main Content - Single screen only */}
      <div className={`${isFullscreen ? 'h-screen' : 'h-[calc(100vh-4.5rem)]'} w-full`}>
        <ScreenDisplay screen={screens[0]} isFullscreen={isFullscreen} />
      </div>


      {/* Exit Fullscreen Button */}
      {isFullscreen && (
        <Button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 bg-gray-800/80 hover:bg-gray-700 text-white backdrop-blur-sm border border-gray-600/50 opacity-40 hover:opacity-100 transition-all duration-300 rounded-full p-3"
          size="icon"
        >
          <Minimize2 className="w-6 h-6" />
        </Button>
      )}
    </div>
  );
}